import { Routes, Route, Navigate, Outlet } from 'react-router-dom'

import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ProtectedRoute } from './components/layout/ProtectedRoute'
import { ROUTE_ACCESS } from './lib/routeAccess'
import { AppLayout } from './components/layout/AppLayout'
import { Login } from './pages/Login'
import { Etusivu } from './pages/Etusivu'
import { Tehtavahallinta } from './pages/Tehtavahallinta'
import { TehtavahallintaMobile } from './pages/TehtavahallintaMobile'
import { Aikataulu } from './pages/Aikataulu'
import { useIsMobile } from './hooks/useIsMobile'
import { Hankkeet } from './pages/Hankkeet'
import { Kayttajat } from './pages/Kayttajat'
import { Tuuraajat } from './pages/Tuuraajat'
import { Tehtavapohjat } from './pages/Tehtavapohjat'
import { Tilannekatsaus } from './pages/Tilannekatsaus'
import { Kehitys } from './pages/Kehitys'
import { BackgroundJobsToasts } from './components/common/BackgroundJobsToasts'
import { Toaster } from 'sonner'


function TehtavahallintaRoute() {
  const isMobile = useIsMobile()
  return isMobile ? <TehtavahallintaMobile /> : <Tehtavahallinta />
}

function MobileRedirect() {
  const isMobile = useIsMobile()
  if (isMobile) return <Navigate to="/tasks" replace />
  return <Outlet />
}

function AppContent() {
  const { isAuthenticated, isLoading } = useAuth()
  const isMobile = useIsMobile()

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  return (
    <>
      {isAuthenticated && <BackgroundJobsToasts />}
      <Toaster position="bottom-right" richColors closeButton />
      <Routes>
      <Route path="/login" element={<Login />} />
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route path="/tasks" element={<TehtavahallintaRoute />} />
          <Route element={<MobileRedirect />}>
            <Route path="/dashboard" element={<Etusivu />} />
            <Route path="/schedule" element={<Aikataulu />} />
            <Route path="/projects" element={<Hankkeet />} />
            {/* Access floors come from ROUTE_ACCESS (lib/routeAccess.ts), the
                same map the sidebar filters on — so a nav item and the route
                behind it can never disagree. The floors themselves match the
                backend's GLOBAL_CAPABILITIES: MANAGE_USERS is ADMIN-only,
                MANAGE_SUBSTITUTIONS and MANAGE_TEMPLATES have an INTERNAL
                floor. Without these a GUEST reached the page and saw CRUD
                buttons every mutation rejects. */}
            <Route
              path="/users"
              element={
                <ProtectedRoute requiredAccessLevel={ROUTE_ACCESS["/users"]}>
                  <Kayttajat />
                </ProtectedRoute>
              }
            />
            <Route
              path="/substitutes"
              element={
                <ProtectedRoute requiredAccessLevel={ROUTE_ACCESS["/substitutes"]}>
                  <Tuuraajat />
                </ProtectedRoute>
              }
            />
            <Route path="/tabs/*" element={<Etusivu />} />
            <Route
              path="/task-templates"
              element={
                <ProtectedRoute requiredAccessLevel={ROUTE_ACCESS["/task-templates"]}>
                  <Tehtavapohjat />
                </ProtectedRoute>
              }
            />
            <Route path="/overview" element={<Tilannekatsaus />} />
            <Route
              path="/feedback"
              element={
                <ProtectedRoute requiredAccessLevel={ROUTE_ACCESS["/feedback"]}>
                  <Kehitys />
                </ProtectedRoute>
              }
            />
          </Route>
        </Route>
      </Route>
      <Route
        path="/"
        element={
          isAuthenticated ? (
            <Navigate to={isMobile ? "/tasks" : "/dashboard"} replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
    </Routes>
    </>
  )
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}

export default App
